// pastCanvasBackground — 過去キャンバス行の snapshot meta から背景色だけ取る
// (荒らし復旧「人を外して戻す」のプレビュー用、vandalism-recovery-ui-2026-09-27.md §3)。
//
// 背景色は画素ではなく meta (`meta.background`)。絵ログを終端側だけ読むと全消し前の
// `whiteboard.background-set` が失われるので、プレビューの初期背景は終端の meta から
// 渡す。レイヤ PNG は要らないので `loadSnapshot` ではなく meta.json だけ fetch する。
// 取れなければ null (呼び出し側は従来のフォールバック = ログからの導出に倒す)。

import { snapshotMetaSchema } from '@oekaki/protocol';
import { type FetchFn, fetchPastCanvasSnapshotUrls } from './pastCanvasClient.js';

export interface PastCanvasBackground {
  readonly rgb: number;
  readonly alpha: number;
}

export interface FetchPastCanvasBackgroundInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly pastCanvasId: string;
  readonly adminToken?: string;
  readonly signal?: AbortSignal;
}

export interface FetchPastCanvasBackgroundDeps {
  readonly fetchFn?: FetchFn;
}

export const fetchPastCanvasBackground = async (
  input: FetchPastCanvasBackgroundInput,
  deps: FetchPastCanvasBackgroundDeps = {},
): Promise<PastCanvasBackground | null> => {
  const fetchFn: FetchFn =
    deps.fetchFn ??
    ((url, init) => globalThis.fetch(url, init as RequestInit | undefined) as Promise<Response>);
  const signed = await fetchPastCanvasSnapshotUrls(input, { fetchFn });
  if (signed.outcome !== 'ok') return null;
  try {
    const res = await fetchFn(
      signed.urls.metaUrl,
      input.signal !== undefined ? { signal: input.signal } : {},
    );
    if (!res.ok) return null;
    const parsed = snapshotMetaSchema.safeParse(await res.json());
    if (!parsed.success) return null;
    return { rgb: parsed.data.background.rgb, alpha: parsed.data.background.alpha };
  } catch {
    return null;
  }
};
